import dayjs from "dayjs";
import { useMemo, useState } from "react";

import { useFitbitActivity } from "../../../../../../hooks/queries/fitbit";
import { useUserPreferences } from "../../../../../../hooks/useUserPreferences";
import { userService } from "../../../../../../services/userService";
import { getShortFormattedDate } from "../../../../../../utils/dateFormatter";
import {
  HeaderContainer,
  MeasurementContainer,
  Subtitle,
  Title,
} from "../../MeasurementModal.styles";
import {
  DATE_FILTER_ALL,
  DATE_FILTER_CUSTOM,
  DateFilterOptionType,
} from "../CustomOptionsBar";
import {
  FitbitActivityChart,
  FitbitChartRow,
} from "../FitbitActivityChart/FitbitActivityChart";
import { FitbitActivityTable } from "../FitbitActivityTable/FitbitActivityTable";
import { SubtitleConatiner } from "../MeasurementSection/MeasurementSection.styles";
import { ModalSkeleton } from "../ModalSkeleton";
import { FitbitOptionsBar } from "./FitbitOptionsBar";

type FitbitSectionProps = {
  patientId?: string;
  cardTitle: string;
  isPinnedGraph?: boolean;
  onSelectedTableView?: (showTable: boolean) => void;
};

export function FitbitSection({
  patientId,
  cardTitle,
  isPinnedGraph,
  onSelectedTableView,
}: FitbitSectionProps) {
  const { getDateRangeFilter, portalPreferences, changePortalPreferences } =
    useUserPreferences();

  const dateRangeFilter = getDateRangeFilter();
  const initialFilter: DateFilterOptionType = {
    endDate: dateRangeFilter.endDate,
    id: dateRangeFilter.id,
    startDate: dateRangeFilter.startDate,
  };

  const [selectedFilterDate, setSelectedFilterDate] =
    useState<DateFilterOptionType>(initialFilter);
  const [showTable, setShowTable] = useState(false);

  const { activityData, isActivityFetching, isActivityRefetching } =
    useFitbitActivity(
      parseInt(patientId ?? "0", 10),
      selectedFilterDate.startDate.startOf("day"),
      selectedFilterDate.endDate.endOf("day"),
      !!patientId && patientId !== "0"
    );

  const chartData: FitbitChartRow[] = useMemo(() => {
    if (!activityData) return [];

    if (activityData.viewMode === "monthly") {
      return (activityData.monthlyReadings ?? []).map((reading) => {
        // Center monthly bars at the middle of the month (15th day, noon)
        // Parse the date and set to 15th day at noon
        const dateParts = reading.monthStart.split("T")[0].split("-");
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // JS months are 0-indexed
        const monthDate = new Date(year, month, 15, 12, 0, 0, 0);
        return {
          timeValue: monthDate.getTime(),
          sedentaryHours: reading.avgSedentaryHours,
          activeHours: reading.avgActiveHours,
          label: reading.monthLabel,
          isMonthly: true,
          sedentaryPercent: reading.sedentaryPercent,
          lightlyActivePercent: reading.lightlyActivePercent,
          fairlyActivePercent: reading.fairlyActivePercent,
          veryActivePercent: reading.veryActivePercent,
          minutesSedentary: reading.avgMinutesSedentary,
          minutesLightlyActive: reading.avgMinutesLightlyActive,
          minutesFairlyActive: reading.avgMinutesFairlyActive,
          minutesVeryActive: reading.avgMinutesVeryActive,
        };
      });
    }

    return (activityData.dailyReadings ?? []).map((reading) => {
      // Center daily bars at noon to align better with X-axis ticks
      // Parse the date string and set time to noon, preserving the calendar day
      // Note: reading.date from backend is like "2025-12-29" (date only)
      const dateParts = reading.date.split("T")[0].split("-");
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // JS months are 0-indexed
      const day = parseInt(dateParts[2], 10);
      const date = new Date(year, month, day, 12, 0, 0, 0);
      return {
        timeValue: date.getTime(),
        sedentaryHours: reading.sedentaryHours,
        activeHours: reading.activeHours,
        isMonthly: false,
        sedentaryPercent: reading.sedentaryPercent,
        lightlyActivePercent: reading.lightlyActivePercent,
        fairlyActivePercent: reading.fairlyActivePercent,
        veryActivePercent: reading.veryActivePercent,
        minutesSedentary: reading.minutesSedentary,
        minutesLightlyActive: reading.minutesLightlyActive,
        minutesFairlyActive: reading.minutesFairlyActive,
        minutesVeryActive: reading.minutesVeryActive,
      };
    });
  }, [activityData]);

  // Compute actual data range from chart data (reliable, no backend dependency)
  const actualDataRange = useMemo(() => {
    if (chartData.length === 0) return null;
    const timestamps = chartData.map((d) => d.timeValue);
    const minTs = Math.min(...timestamps);
    const maxTs = Math.max(...timestamps);
    return {
      startDate: dayjs(minTs),
      endDate: dayjs(maxTs),
    };
  }, [chartData]);

  // For "ALL" filter, show actual data range in subtitle; otherwise show selected filter
  const displayDateRange = useMemo(() => {
    if (selectedFilterDate.id === DATE_FILTER_ALL && actualDataRange) {
      return actualDataRange;
    }
    return selectedFilterDate;
  }, [selectedFilterDate, actualDataRange]);

  const formattedDateRange = `${getShortFormattedDate(
    displayDateRange.startDate.format()
  )} - ${getShortFormattedDate(displayDateRange.endDate.format())}`;

  const handleFilterChange = (selectedDateFilter: DateFilterOptionType) => {
    const { id } = selectedDateFilter;
    if (id === DATE_FILTER_CUSTOM || id !== selectedFilterDate.id) {
      setSelectedFilterDate(selectedDateFilter);

      // Save to global portal preferences (like MeasurementSection does)
      const newPortalPreferences = {
        ...portalPreferences,
        dateRangeFilter: id,
        customFromDate:
          id === DATE_FILTER_CUSTOM ? selectedDateFilter.startDate : undefined,
        customToDate:
          id === DATE_FILTER_CUSTOM ? selectedDateFilter.endDate : undefined,
      };
      changePortalPreferences(newPortalPreferences);

      // Persist to backend (except for custom which is session-only)
      if (id !== DATE_FILTER_CUSTOM) {
        userService.updateDateRangeFilter(id);
      }
    }
  };

  const handleTableViewChange = (enableTableView: boolean) => {
    setShowTable(enableTableView);
    onSelectedTableView?.(enableTableView);
  };

  const isLoading = isActivityFetching || isActivityRefetching;
  const isEmpty =
    !activityData ||
    chartData.length === 0 ||
    chartData.every(
      (d) => (d.activeHours ?? 0) === 0 && (d.sedentaryHours ?? 0) === 0
    );

  if (isLoading) {
    return <ModalSkeleton isPinnedGraph={isPinnedGraph} width={968} />;
  }

  return (
    <MeasurementContainer>
      <HeaderContainer style={{ marginBottom: showTable ? "0px" : "20px" }}>
        <Title>{cardTitle}</Title>
        <Subtitle>{formattedDateRange}</Subtitle>

        <SubtitleConatiner>
          <FitbitOptionsBar
            onChange={handleFilterChange}
            onTableViewChange={handleTableViewChange}
            selectedFilterDate={selectedFilterDate}
            showTable={showTable}
            isEmpty={isEmpty}
          />
        </SubtitleConatiner>
      </HeaderContainer>

      {showTable ? (
        <FitbitActivityTable
          data={chartData}
          viewMode={activityData?.viewMode ?? "daily"}
          isPinnedGraph={isPinnedGraph}
        />
      ) : (
        <FitbitActivityChart
          data={chartData}
          sources={activityData?.sources ?? []}
          dateInterval={selectedFilterDate}
          height="78%"
          width={isPinnedGraph ? "100%" : 968}
          isPinnedGraph={isPinnedGraph}
          isEmpty={isEmpty}
        />
      )}
    </MeasurementContainer>
  );
}
